import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';

import { Announcement, Button, Input, ModalV2 as Modal } from '@utilities';

import { DEFAULT_GROUP_NAME } from '@api/instant_survey';

import styles from './_index.module.scss';

const buildDuplicateName = (baseName, groups) => {
  const trimmedBase = (baseName || DEFAULT_GROUP_NAME).trim();
  const existingNames = new Set(
    groups.filter(({ _delete }) => !_delete).map(({ name }) => name.trim())
  );

  let candidate = `${trimmedBase} (duplicate)`;
  let suffix = 2;

  while (existingNames.has(candidate)) {
    candidate = `${trimmedBase} (duplicate ${suffix})`;
    suffix += 1;
  }

  return candidate;
};

const ModalDuplicateGroup = ({ group, groups, onClose, onSubmit, remainingAvailableResponses }) => {
  const defaultName = useMemo(() => buildDuplicateName(group.name, groups), [group.name, groups]);
  const [newGroup, setNewGroup] = useState({
    ...group,
    clientId: nanoid(),
    id: null,
    name: defaultName,
    quotaLimit: Math.min(group.quotaLimit, remainingAvailableResponses),
  });

  const trimmedName = newGroup.name.trim();
  const isNameUnique = !groups
    .filter(({ _delete }) => !_delete)
    .some(({ name }) => name.trim() === trimmedName);

  return (
    <Modal
      buttons={[
        <Button
          id="modal-duplicate-group-cancel"
          key="cancel-btn"
          onClick={onClose}
          text="Cancel"
          variant="secondary"
        />,
        <Button
          id="modal-duplicate-group-continue"
          isDisabled={!newGroup.name.trim() || !isNameUnique}
          key="submit-btn"
          onClick={() => onSubmit(newGroup)}
          text="Create"
        />,
      ]}
      id="modal-duplicate-group"
      isActive
      onClose={onClose}
      title="Duplicate Quota Group"
    >
      {group.quotaLimit !== newGroup.quotaLimit && (
        <Announcement
          text={`Responses for this group will be set to ${newGroup.quotaLimit} as you will reach the maximum allowed.`}
          variant="warn"
        />
      )}
      <p className={styles['modal-duplicate-group-p']}>
        The name for this quota group will appear on your report: it cannot be edited once the
        survey is set active.
      </p>
      <Input
        className={styles['modal-duplicate-group-input']}
        error={isNameUnique ? '' : 'A group with that name already exists'}
        label="Name"
        onChange={(value) => setNewGroup({ ...newGroup, name: value })}
        placeholder={DEFAULT_GROUP_NAME}
        value={newGroup.name}
      />
    </Modal>
  );
};

ModalDuplicateGroup.propTypes = {
  group: PropTypes.object.isRequired,
  groups: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  remainingAvailableResponses: PropTypes.number,
};

export default ModalDuplicateGroup;
